import axios, { type AxiosError } from 'axios'

const baseURL = import.meta.env.VITE_API_URL ?? '/api/v1'

export const ACCESS_TOKEN_STORAGE_KEY = 'dealer-commerce-access-token'
export const REFRESH_TOKEN_STORAGE_KEY = 'dealer-commerce-refresh-token'

const RETRY_FLAG = '_authRefreshRetried'

export function getAccessToken(): string | null {
  return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)
}

export function getRefreshToken(): string | null {
  return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
}

export function storeTokens(accessToken: string, refreshToken?: string | null): void {
  window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken)
  if (refreshToken !== undefined) {
    if (refreshToken) {
      window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken)
    } else {
      window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
    }
  }
}

export function clearTokens(): void {
  window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
  window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
}

let refreshInFlight: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    clearTokens()
    return null
  }

  if (!refreshInFlight) {
    refreshInFlight = axios
      .post<{ success: boolean; data: { accessToken: string; refreshToken: string } }>(
        `${baseURL}/auth/refresh`,
        { refreshToken },
        { timeout: 15_000 },
      )
      .then((response) => {
        const { accessToken, refreshToken: nextRefreshToken } = response.data.data
        storeTokens(accessToken, nextRefreshToken)
        return accessToken
      })
      .catch(() => {
        clearTokens()
        return null
      })
      .finally(() => {
        refreshInFlight = null
      })
  }

  return refreshInFlight
}

export const http = axios.create({
  baseURL,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
})

http.interceptors.request.use(
  (config) => {
    const token = getAccessToken()
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`)
    }
    return config
  },
  (error) => Promise.reject(error),
)

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config

    if (config && error.response?.status === 401 && !config.headers?.[RETRY_FLAG]) {
      const newToken = await refreshAccessToken()
      if (newToken) {
        config.headers.set(RETRY_FLAG, 'true')
        config.headers.set('Authorization', `Bearer ${newToken}`)
        return http.request(config)
      }
    }
    return Promise.reject(error)
  },
)

export interface ApiErrorBody {
  success?: boolean
  message?: string
  error?: { code?: string; message?: string }
}

/** Extract a stable error message from an Axios error. */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    return error.response?.data?.message ?? error.message ?? fallback
  }
  if (error instanceof Error) {
    return error.message
  }
  return fallback
}

export function getApiErrorCode(error: unknown): string | undefined {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    return error.response?.data?.error?.code
  }
  return undefined
}